
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  SELLER = 'SELLER',
  CUSTOMER = 'CUSTOMER',
  STAFF = 'STAFF'
}

export enum StaffRole {
  MANAGER = 'MANAGER',
  FULFILLMENT = 'FULFILLMENT',
  SUPPORT = 'SUPPORT'
}

export enum SellingModel {
  INTERNAL = 'INTERNAL',
  AFFILIATE = 'AFFILIATE'
}

export type SubscriptionTier = 'STARTER' | 'PRO' | 'ENTERPRISE';

export interface SubscriptionPlan {
  id: SubscriptionTier;
  name: string;
  price: number;
  productLimit: number;
  features: string[];
  color: string;
}

export interface StoreSubscription {
  tier: SubscriptionTier;
  startDate: string;
  expiryDate: string; // Used as Next Billing Date
  lastBilledAt?: string;
  autoRenew: boolean;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'OVERDUE';
}

export interface StoreBranding {
  primaryColor: string;
  accentColor: string;
  slogan?: string;
  bannerImage?: string;
  fontFamily?: string;
  buttonRadius?: 'rounded-none' | 'rounded-xl' | 'rounded-3xl' | 'rounded-full';
  glassmorphism?: boolean;
  themeMode?: 'light' | 'dark' | 'glass';
}

export interface SocialProofConfig {
  enabled: boolean;
  showRecentSales: boolean;
  showStockAlerts: boolean;
  showLiveViewers: boolean;
  intervalSeconds: number;
}

export interface DynamicPricingConfig {
  enabled: boolean;
  maxDiscountPercentage: number;
  minDiscountPercentage: number;
  strategy: 'AGGRESSIVE' | 'BALANCED' | 'CONSERVATIVE';
  autoApply: boolean;
}

export interface FlashSale {
  id: string;
  storeId: string;
  name: string;
  discountPercentage: number;
  startTime: string;
  endTime: string;
  productIds: string[];
  status: 'ACTIVE' | 'EXPIRED' | 'SCHEDULED';
}

export interface WishlistItem {
  id: string;
  userId: string;
  productId: string;
  storeId: string;
  addedAt: string;
}

export type UserInteractionType = 'VIEW' | 'ADD_TO_CART' | 'WISHLIST_ADD' | 'SEARCH' | 'AR_TRY_ON' | 'AUCTION_BID';

export interface UserInteraction {
  userId: string;
  productId: string;
  type: UserInteractionType;
  timestamp: string;
}

export interface Bid {
  id: string;
  userId: string;
  userName: string;
  amount: number;
  timestamp: string;
}

export interface AuctionEvent {
  id: string;
  storeId: string;
  productId: string;
  startTime: string;
  endTime: string;
  startingPrice: number;
  currentPrice: number;
  bidIncrement: number;
  bids: Bid[];
  status: 'ACTIVE' | 'ENDED' | 'SCHEDULED';
  videoUrl?: string;
}

export interface SocialVideo {
  id: string;
  storeId: string;
  productId: string;
  videoUrl: string;
  thumbnailUrl: string;
  caption: string;
  views: number;
  likes: number;
  shares: number;
  conversionRate: number;
  status: 'ACTIVE' | 'ARCHIVED';
}

export interface Store {
  id: string;
  name: string;
  slug: string;
  logo?: string;
  branding?: StoreBranding;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED';
  ownerId?: string;
  createdAt?: string;
  wallet?: Wallet;
  payoutMethod?: PayoutMethod;
  commissionRate?: number;
  subscription?: StoreSubscription;
  taxConfig?: StoreTaxConfig;
  kyc?: StoreKYC;
  shippingConfig?: StoreShippingConfig;
  socialProof?: SocialProofConfig;
  dynamicPricing?: DynamicPricingConfig;
  staff?: StoreStaff[];
  influencers?: Influencer[];
  vipConfigs?: VIPConfig[];
}

export interface Product {
  id: string;
  storeId: string;
  categoryId: string;
  name: string;
  description: string;
  price: number;
  stock: number;
  images: string[];
  videoUrl?: string; 
  createdAt?: string;
  sellingModel: SellingModel;
  externalUrl?: string;
  affiliatePlatform?: string;
  hsnCode?: string;
  attributes?: ProductAttribute[];
  variants?: ProductVariant[];
  rating?: number;
  reviewCount?: number;
  wishlistCount?: number;
  dynamicPriceAdjustment?: number;
  isARReady?: boolean;
}

export interface Order {
  id: string;
  storeId: string;
  userId: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  platformCommission: number;
  sellerNet: number;
  status: OrderStatus;
  paymentMethod?: 'CARD' | 'UPI' | 'COINS';
  createdAt: string;
  settledAt?: string;
  location?: { city: string; country: string; lat: number; lng: number }; 
  coinsEarned?: number;
  coinsRedeemed?: number;
  items?: CartItem[];
  taxRate?: number;
  updatedAt?: string;
  shippingMethodId?: string;
  shippingCost?: number;
  couponId?: string;
  discountAmount?: number;
  influencerId?: string;
  influencerCommission?: number;
  vipDiscountAmount?: number;
  vipTierAtPurchase?: VIPTierLevel;
}

export type OrderStatus = 'PENDING' | 'PAID' | 'PROCESSING' | 'SHIPPED' | 'OUT_FOR_DELIVERY' | 'DELIVERED' | 'CANCELLED' | 'RETURN_REQUESTED' | 'RETURNED';
export type CouponType = 'PERCENTAGE' | 'FIXED';
export type SubscriptionFrequency = 'MONTHLY' | 'QUARTERLY';
export type VIPTierLevel = 'NONE' | 'SILVER' | 'GOLD' | 'PLATINUM';

export interface Wallet { pendingBalance: number; availableBalance: number; totalWithdrawn: number; lastPayoutDate?: string; }
export interface PayoutMethod { type: 'BANK' | 'UPI' | 'PAYPAL'; accountName: string; accountNumber?: string; routingNumber?: string; bankName?: string; upiId?: string; isVerified?: boolean; }
export interface StoreTaxConfig { enabled: boolean; taxNumber?: string; taxRate: number; businessAddress?: string; }
export interface StoreKYC { businessType: 'INDIVIDUAL' | 'COMPANY'; fullName: string; idProofUrl?: string; addressProofUrl?: string; submittedAt?: string; verifiedAt?: string; rejectionReason?: string; }
export interface StoreStaff { id: string; email: string; role: StaffRole; addedAt: string; status: 'ACTIVE' | 'INVITED'; }
export interface StaffActivity { id: string; staffId: string; staffEmail: string; action: string; timestamp: string; }
export interface Influencer { id: string; storeId: string; name: string; handle: string; commissionRate: number; totalSalesGenerated: number; totalCommissionEarned: number; isActive: boolean; onboardedAt: string; }
export interface VIPConfig { level: VIPTierLevel; spendThreshold: number; discountPercentage: number; color: string; label: string; }
export interface Category { id: string; storeId: string; name: string; description?: string; }
export interface ProductAttribute { name: string; options: string[]; }
export interface ProductVariant { id: string; name: string; price: number; stock: number; sku?: string; attributes: Record<string, string>; }
export interface Review { id: string; productId: string; userId: string; userName: string; rating: number; comment: string; isVerified: boolean; createdAt: string; }
export interface ReturnRequest { id: string; orderId: string; storeId: string; reason: string; status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REFUNDED'; requestedAt: string; processedAt?: string; items: CartItem[]; }
export interface AffiliateClick { id: string; productId: string; storeId: string; timestamp: string; userId?: string; }
export interface CartItem { productId: string; variantId?: string; quantity: number; product: Product; variant?: ProductVariant; }
export interface TaxReport { totalTaxableValue: number; totalTaxCollected: number; totalCommissionTax?: number; period: string; }
export interface PayoutRequest { id: string; storeId: string; amount: number; status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID'; requestedAt: string; processedAt?: string; notes?: string; payoutMethodSnapshot?: PayoutMethod; }
export interface CoinTransaction { id: string; userId: string; amount: number; type: 'EARN' | 'REDEEM' | 'BONUS'; orderId?: string; description: string; timestamp: string; }
export interface Promotion { id: string; title: string; subtitle: string; imageUrl: string; linkUrl: string; type: 'HERO' | 'BANNER' | 'POPUP'; status: 'ACTIVE' | 'EXPIRED' | 'DRAFT'; priority: number; startDate?: string; endDate?: string; }
export interface User { id: string; email: string; role: UserRole; storeId?: string; coins?: number; staffRole?: StaffRole; }
export interface Message { id: string; senderId: string; receiverId: string; text: string; isAi?: boolean; timestamp: string; }
export interface Conversation { id: string; customerId: string; storeId: string; messages: Message[]; lastMessageAt: string; }
export interface PlatformConfig { globalCommissionRate: number; settlementDays: number; }
export interface SubscriptionBox { id: string; storeId: string; name: string; description: string; price: number; frequency: SubscriptionFrequency; items: string[]; imageUrl: string; isActive: boolean; }
export interface UserSubscription { id: string; userId: string; storeId: string; boxId: string; status: 'ACTIVE' | 'CANCELLED' | 'PAUSED'; startDate: string; nextBillingDate: string; box?: SubscriptionBox; }
export interface ShippingMethod { id: string; name: string; description: string; baseCost: number; minSubtotalForFree?: number; estimatedDays: string; isActive: boolean; }
export interface StoreShippingConfig { methods: ShippingMethod[]; }
export interface Coupon { id: string; code: string; type: CouponType; value: number; minOrderValue: number; expiryDate: string; storeId: string; usageCount: number; maxUsage?: number; isActive: boolean; }

// Added missing interfaces to resolve export errors
export interface Bundle {
  id: string;
  storeId: string;
  name: string;
  productIds: string[];
  discountPercentage: number;
  isActive: boolean;
}

export interface LandingPage {
  id: string;
  storeId: string;
  slug: string;
  title: string;
  heroHeadline: string;
  heroSubheadline: string;
  benefits: { title: string; description: string }[];
  productIds: string[];
  ctaText: string;
  themeColor: string;
  status: 'DRAFT' | 'PUBLISHED';
  createdAt: string;
  views: number;
  conversions: number;
}
